import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, 
  FormControlLabel, Switch, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Divider,
  Autocomplete, Chip, FormHelperText, useTheme, Grid,
  Alert, Snackbar, Tooltip, IconButton, Popover,
  Fade, Zoom, Grow, Card, CardContent, alpha, Avatar
} from '@mui/material';
import {
  Send as SendIcon,
  Campaign as CampaignIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEmotions as EmojiIcon,
  Person as PersonIcon,
  PeopleAlt as PeopleAltIcon,
  Title as TitleIcon,
  Create as CreateIcon,
  FormatColorText as FormatColorTextIcon
} from '@mui/icons-material';
import EmojiPicker from 'emoji-picker-react';
import { motion } from 'framer-motion';
import messageService from '../../../services/messageService';
import memberService from '../../../services/memberService';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';

const AdminComposePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useNotification();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const isStaffPortal = location.pathname.startsWith('/staff/') || user?.role === 'staff';
  
  // Animation states
  const [showContent, setShowContent] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  useEffect(() => {
    // Add animation sequence
    const timer1 = setTimeout(() => {
      setShowContent(true);
    }, 100);
    
    const timer2 = setTimeout(() => {
      setShowForm(true);
    }, 300);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const showLocalNotification = (message, severity = 'info') => {
    if (notification) {
      if (typeof notification.notify === 'function') {
        notification.notify(message, severity);
        return;
      } else if (typeof notification.showNotification === 'function') {
        notification.showNotification(message, severity);
        return;
      } else if (typeof notification.setNotification === 'function') {
        notification.setNotification({ message, severity, open: true });
        return;
      }
    }
    
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isBroadcast, setIsBroadcast] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Add states for emoji picker
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const openEmojiPicker = Boolean(emojiAnchorEl);

  // Function to handle opening emoji picker
  const handleEmojiPickerOpen = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  // Function to handle closing emoji picker
  const handleEmojiPickerClose = () => {
    setEmojiAnchorEl(null);
  };

  // Function to handle emoji selection
  const onEmojiClick = (emojiObject) => {
    setMessage((prevMessage) => prevMessage + emojiObject.emoji);
    if (errors.message) {
      setErrors({ ...errors, message: undefined });
    }
  };

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const response = await memberService.getAllMembers();
        
        if (response.success) {
          setMembers(response.data || []);
        } else {
          console.error('Failed to fetch members:', response.error);
          showLocalNotification('Failed to load members', 'error');
        }
      } catch (err) {
        console.error('Error fetching members:', err);
        showLocalNotification('Failed to load members', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    if (!isBroadcast) {
      fetchMembers();
    }
  }, [isBroadcast]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    if (!isBroadcast && !selectedMember) {
      newErrors.selectedMember = 'Please select a recipient';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSendMessage = async () => {
    if (!validateForm()) return;
    
    setSendingMessage(true);
    try {
      const messageData = {
        subject,
        text: message,
        isBroadcast,
      };
      
      if (!isBroadcast && selectedMember) {
        messageData.receiverId = selectedMember._id;
      }
      
      const response = await messageService.sendMessage(messageData);
      
      if (response.success) {
        showLocalNotification(
          isBroadcast ? 'Broadcast message sent successfully' : 'Message sent successfully', 
          'success'
        );
        navigate(isStaffPortal ? '/staff/messages' : '/admin/messages');
      } else {
        showLocalNotification(response.error || 'Failed to send message', 'error');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      showLocalNotification('An error occurred while sending the message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };
  
  const handleCancel = () => {
    // Navigate back to the messages list with the correct role context
    navigate(isStaffPortal ? '/staff/messages' : '/admin/messages');
  };
  
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Fade in={showContent} timeout={800}>
        <Box>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleCancel}
            sx={{ 
              mb: 3,
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                transform: 'translateX(-5px)'
              }
            }}
          >
            Back to Messages
          </Button>
        </Box>
      </Fade>
      
      <Zoom in={showContent} timeout={800}>
        <Paper 
          elevation={3}
          sx={{ 
            borderRadius: 2, 
            overflow: 'hidden',
            boxShadow: theme.shadows[3],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Header */}
          <Box sx={{ 
            p: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
          }}>
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold', 
                  display: 'flex', 
                  alignItems: 'center',
                  color: theme.palette.text.primary
                }}
              >
                {isBroadcast ? (
                  <CampaignIcon 
                    sx={{ mr: 1.5, fontSize: 32, color: theme.palette.info.main }} 
                  />
                ) : (
                  <SendIcon 
                    sx={{ mr: 1.5, fontSize: 32, color: theme.palette.primary.main }} 
                  />
                )}
                {isBroadcast ? 'Compose Broadcast Message' : 'Compose Direct Message'}
              </Typography>
            </motion.div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ mt: 1, maxWidth: 800 }}
              >
                {isBroadcast 
                  ? 'Send an announcement to all members of this platform. Broadcast messages are a great way to share important updates, events, or news.'
                  : 'Send a direct message to a specific member. Direct messages are private conversations between you and the member.'}
              </Typography>
            </motion.div>
          </Box>
          
          <Grow in={showForm} timeout={1000}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Message Type Switch */}
                <Grid item xs={12}>
                  <Card 
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden', 
                      transition: 'all 0.3s ease-in-out',
                      bgcolor: isBroadcast 
                        ? alpha(theme.palette.info.main, 0.08)
                        : alpha(theme.palette.primary.main, 0.08),
                      border: `1px solid ${isBroadcast 
                        ? alpha(theme.palette.info.main, 0.2)
                        : alpha(theme.palette.primary.main, 0.2)
                      }`,
                      '&:hover': {
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isBroadcast}
                            onChange={(e) => {
                              setIsBroadcast(e.target.checked);
                              setSelectedMember(null);
                              setErrors({...errors, selectedMember: undefined});
                            }}
                            color={isBroadcast ? "info" : "primary"}
                            sx={{ 
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: isBroadcast ? theme.palette.info.main : theme.palette.primary.main,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: isBroadcast ? theme.palette.info.main : theme.palette.primary.main,
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isBroadcast ? (
                              <>
                                <CampaignIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                                <Typography sx={{ fontWeight: 500, color: theme.palette.info.dark }}>
                                  Broadcast to all members
                                </Typography>
                              </>
                            ) : (
                              <>
                                <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                <Typography sx={{ fontWeight: 500, color: theme.palette.primary.dark }}>
                                  Direct message to specific member
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Recipient Selection */}
                {!isBroadcast && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <PersonIcon sx={{ mr: 1, mt: 0.5, color: theme.palette.primary.main }} />
                      <Typography variant="subtitle1" fontWeight="500" color="primary">
                        Select Recipient
                      </Typography>
                    </Box>
                    
                    <Autocomplete
                      value={selectedMember}
                      onChange={(event, newValue) => {
                        setSelectedMember(newValue);
                        if (newValue) {
                          setErrors({...errors, selectedMember: undefined});
                        }
                      }}
                      options={members}
                      getOptionLabel={(option) => 
                        `${option.userId?.firstName || ''} ${option.userId?.lastName || ''} (${option.userId?.email || ''})`
                      }
                      loading={loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select a member..."
                          variant="outlined"
                          fullWidth
                          error={!!errors.selectedMember}
                          helperText={errors.selectedMember}
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.background.paper, 0.5),
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                              },
                              '&.Mui-focused': {
                                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                              }
                            },
                            startAdornment: (
                              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, mr: -0.5 }}>
                                <PersonIcon color="action" />
                              </Box>
                            ),
                            endAdornment: (
                              <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                width: 36,
                                height: 36,
                                mr: 1.5
                              }}
                            >
                              {option.userId?.firstName?.charAt(0) || 'M'}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {option.userId?.firstName || ''} {option.userId?.lastName || ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.userId?.email || 'No email available'}
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      noOptionsText={
                        <Box sx={{ p: 1, textAlign: 'center' }}>
                          <PeopleAltIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography>No members found</Typography>
                        </Box>
                      }
                      loadingText={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          <Typography>Loading members...</Typography>
                        </Box>
                      }
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          borderRadius: 16,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        }
                      }}
                    />
                    {members.length === 0 && !loading && (
                      <FormHelperText error sx={{ ml: 2, mt: 1 }}>
                        No members found in the tenant. Please add members first.
                      </FormHelperText>
                    )}
                  </Grid>
                )}
                
                {/* Subject Field */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <TitleIcon sx={{ mr: 1, mt: 0.5, color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight="500" color="primary">
                      Subject
                    </Typography>
                  </Box>
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter the subject of your message"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      if (e.target.value.trim()) {
                        setErrors({...errors, subject: undefined});
                      }
                    }}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    InputProps={{
                      sx: { 
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.background.paper, 0.8),
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      },
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, mr: -0.5 }}>
                          <FormatColorTextIcon color="action" />
                        </Box>
                      ),
                    }}
                  />
                </Grid>
                
                {/* Message Field */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <CreateIcon sx={{ mr: 1, mt: 0.5, color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight="500" color="primary">
                      Message
                    </Typography>
                  </Box>
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={8}
                    placeholder="Write your message here..."
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      if (e.target.value.trim()) {
                        setErrors({...errors, message: undefined});
                      }
                    }}
                    error={!!errors.message}
                    helperText={errors.message}
                    InputProps={{
                      sx: { 
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.background.paper, 0.8),
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title="Add Emoji">
                      <IconButton 
                        onClick={handleEmojiPickerOpen}
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          }
                        }}
                      >
                        <EmojiIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
                
                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 2, 
                    mt: 2,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                    pt: 3
                  }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCancel}
                      sx={{ 
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                        borderColor: alpha(theme.palette.text.primary, 0.3),
                        color: theme.palette.text.primary
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color={isBroadcast ? "info" : "primary"}
                      startIcon={isBroadcast ? <CampaignIcon /> : <SendIcon />}
                      onClick={handleSendMessage}
                      disabled={sendingMessage}
                      sx={{ 
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                        boxShadow: theme.shadows[3],
                        background: isBroadcast 
                          ? `linear-gradient(45deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
                          : `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        '&:hover': {
                          boxShadow: theme.shadows[5],
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      {sendingMessage ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : isBroadcast ? (
                        'Send Broadcast'
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grow>
        </Paper>
      </Zoom>
      
      {/* Emoji Picker */}
      <Popover
        open={openEmojiPicker}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[5],
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }
        }}
      >
        <EmojiPicker 
          onEmojiClick={onEmojiClick} 
          lazyLoadEmojis={true}
        />
      </Popover>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[3]
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminComposePage;